import clsx from 'clsx';
import * as React from 'react';
import { useState, useCallback } from 'react';
import { observer } from 'mobx-react';
import { Button } from '@execonline-inc/execonline-ui';
import Navigation from './Navigation';
import Beta from './Beta';
import ReportStore from '../../ReportStore';
import QuickSightDashboardWithSummary from './QuickSightDashboardWithSummary';
import { AIButtonState } from './QuickSightDashboardWithSummary/types';
import SparklesIcon from '../Cutesies/SparklesIcon';
import { findPayload } from '@execonline-inc/collections';
import { ReportWithDashboards, ReportResource } from '../../ReportStore/Types';
import { T, NotTranslated } from '../../Translations';

interface Props {
  store: ReportStore;
  currentDashboardId: number;
  report: ReportWithDashboards;
  reportResource: ReportResource;
}

const ReportImpl: React.FC<Props> = ({ store, report, currentDashboardId, reportResource }) => {
  // State for AI button - lifted from QuickSightDashboardWithSummary
  const [aiButtonState, setAIButtonState] = useState<AIButtonState | null>(null);

  const handleAIButtonStateChange = useCallback((state: AIButtonState) => {
    setAIButtonState(state);
  }, []);

  return (
    <>
      <div
        className={clsx(
          'rounded-none border-t-1 border-b-1',
          'px-2.5 lg:px-0 border-gray-200 bg-white pt-5',
        )}
      >
        <div className="max-w-clientPortalMaxWidth mx-auto lg:px-12">
          {/* Header row with AI button */}
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center">
              <h1 className="text-xl font-semibold leading-7 text-gray-100">
                <NotTranslated text={report.name} />
              </h1>
              <Beta beta={report.beta} />
            </div>
            {/* AI Analysis Button - only show for QuickSight dashboards with Action Plan sheet */}
            {aiButtonState?.hasActionPlanSheet && (
              <Button
                color="secondary"
                size="sm"
                variant="outlined"
                onClick={aiButtonState.openDrawer}
                className="flex items-center gap-2 bg-teal-600 text-white hover:bg-teal-600/80 border-0"
                data-intercom-target="ai-analysis-button"
              >
                <SparklesIcon className="w-4 h-4" color="#fff" />
                AI Action Plan Analysis
              </Button>
            )}
          </div>
          <Navigation
            store={store}
            dashboards={report.dashboards}
            currentDashboardId={currentDashboardId}
          />
        </div>
      </div>
      <div className="px-2.5 lg:px-12">
        {findPayload(currentDashboardId, report.dashboards)
          .map((dashboard) => {
            switch (dashboard.payload.dashboardType) {
              case 'quicksight':
                return (
                  <QuickSightDashboardWithSummary
                    currentDashboardId={currentDashboardId}
                    dashboards={report.dashboards}
                    reportResource={reportResource}
                    onAIButtonStateChange={handleAIButtonStateChange}
                  />
                );
              default:
                return <></>;
            }
          })
          .getOrElse(() => (
            <p className="py-2.5">
              <T kind="Dashboard not found" />
            </p>
          ))}
      </div>
    </>
  );
};

export default observer(ReportImpl);
